import React, { forwardRef, createContext, useContext, useCallback } from 'react';
import type { TabNavLinkComponentProps } from '@withjoy/joykit/components/TabNav';
import { Link } from '@shared/core';

/**
 * Adapter component that wraps react-router Link to match TabNav's expected interface.
 * Maps `href` prop to `to` prop and handles click events for telemetry.
 */
export const RouterLinkAdapter = forwardRef<HTMLAnchorElement, TabNavLinkComponentProps & { onNavigate?: (href: string) => void }>(
  ({ href, children, onNavigate, onClick, ...rest }, ref) => {
    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);
        onNavigate?.(href);
      },
      [href, onClick, onNavigate],
    );

    return (
      <Link
        ref={ref}
        to={href}
        onClick={handleClick}
        {...rest}
      >
        {children}
      </Link>
    );
  },
);
RouterLinkAdapter.displayName = 'RouterLinkAdapter';

/**
 * Context to provide the navigate callback ref to LinkWithTelemetry
 * without passing unknown props through TabNav's linkComponent.
 */
export const NavigateRefContext = createContext<React.MutableRefObject<(href: string) => void>>({
  current: () => {},
});

/**
 * Module-scope link component that reads the navigate callback from context.
 * Avoids creating a new component type inside render (useMemo identity is not guaranteed).
 */
export const LinkWithTelemetry = forwardRef<HTMLAnchorElement, TabNavLinkComponentProps>((linkProps, ref) => {
  const onNavigateRef = useContext(NavigateRefContext);
  return (
    <RouterLinkAdapter
      ref={ref}
      {...linkProps}
      onNavigate={href => onNavigateRef.current(href)}
    />
  );
});
LinkWithTelemetry.displayName = 'LinkWithTelemetry';
